import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { permissionsApi } from '../api/permissions.api'
import { queryKeys } from '../api/queryKeys'
import { useAuthStore } from '../stores/authStore'
import { useEnvStore } from '../stores/envStore'
import {
  ADMIN_SECTIONS,
  fallbackPermissionsFor,
  firstAllowedPath,
  isAccessLevel,
  levelAllows,
  type AdminAccessLevel,
  type AdminAction,
  type AdminSection,
  type PermissionMap,
} from '../config/permissions'

/**
 * Fetches the signed-in admin's row of the permission matrix, never throwing.
 * `null` means "use the built-in matrix" — the endpoint is missing (a production
 * API older than the matrix answers 404), errored, or answered in a shape we
 * don't recognise. Nobody is ever locked out because of this call.
 */
async function fetchPermissions(): Promise<ServerPermissions | null> {
  try {
    const { data } = await permissionsApi.get()
    const raw = data?.data?.permissions
    if (!raw || typeof raw !== 'object') return null
    const out: Partial<PermissionMap> = {}
    for (const section of ADMIN_SECTIONS) {
      const level = raw[section]
      if (isAccessLevel(level)) out[section] = level
    }
    if (Object.keys(out).length === 0) return null
    const badges = data.data.capabilities?.verificationBadges
    const control = data.data.capabilities?.adminControl
    const supply = data.data.capabilities?.supplyInsights
    const roles = data.data.roles
    return {
      permissions: out,
      verificationBadges: typeof badges === 'boolean' ? badges : null,
      adminControl: typeof control === 'boolean' ? control : null,
      supplyInsights: typeof supply === 'boolean' ? supply : null,
      roles: Array.isArray(roles) ? roles.filter((r): r is string => typeof r === 'string') : null,
    }
  } catch {
    return null
  }
}

interface ServerPermissions {
  permissions: Partial<PermissionMap>
  /** null = an API older than the badge allowlist; fall back to the role. */
  verificationBadges: boolean | null
  /** null = an API older than the restricted cards (2026-10-29). */
  adminControl: boolean | null
  /** null = an API older than the Supplier role's supply counts (2026-11-03). */
  supplyInsights: boolean | null
  /** null = an API older than the Creatives role (2026-11-04), which doesn't list its roles. */
  roles: string[] | null
}

export interface Permissions {
  permissions: PermissionMap
  level: (section: AdminSection) => AdminAccessLevel
  can: (section: AdminSection, action: AdminAction) => boolean
  /** The first page this role may open, `/security` as last resort. */
  homePath: string
  /**
   * True once the API has answered GET /admin/permissions, i.e. it runs the
   * permission matrix and accepts the newer roles (manager, editor,
   * content_moderator). False against an older API — production until the
   * roles release — and while the request is in flight.
   */
  apiHasPermissions: boolean
  /**
   * May grant or remove verification badges: Users approve **and** on
   * ADMIN_CONTROL_ALLOWED_EMAILS (Todd and Jesse). Against an API older than that
   * allowlist it falls back to the role, which is all that API checks.
   */
  canSetVerificationBadges: boolean
  /**
   * On ADMIN_CONTROL_ALLOWED_EMAILS (Todd and Jesse): shows the restricted cards
   * on open pages (Suppliers, Users). The API leaves their data out for everyone
   * else, so this only decides what to draw. False while loading and against an
   * older API, so the cards never flash up for the wrong admin.
   */
  isControlAdmin: boolean
  /**
   * Sees the Supply Dashboard's and OneMall's restricted **counts** (enquiries,
   * buyers, searches, top lists) and the OneMall Products / Sellers / Enquiries
   * pages: ADMIN_CONTROL_ALLOWED_EMAILS and the Supplier role (Todd, 3 Nov 2026).
   * Money cards (catalogue value, enquiry value) still need `isControlAdmin`.
   * Against an API older than this it falls back to `isControlAdmin`, which is
   * all that API allows.
   */
  canSeeSupplyInsights: boolean
  /**
   * The API knows about the restricted cards and the Wallet unlock. False against
   * an older API (production until it ships), where the Wallet page works as before.
   */
  apiHasAdminControl: boolean
  /**
   * The stored roles the API accepts (GET /admin/permissions `roles`), so the Admins
   * page only offers roles it can save. null against an older API and while loading.
   */
  apiRoles: string[] | null
  /** GET /admin/permissions has answered (or failed). Until then `apiHasAdminControl` isn't known. */
  permissionsLoaded: boolean
}

/**
 * The signed-in admin's permissions: the server's answer when it gives one, the
 * built-in copy in config/permissions.ts otherwise — including while the request
 * is in flight, so pages never flash or redirect on load. Fetched once per
 * session (per admin and API environment).
 */
export function usePermissions(): Permissions {
  const admin = useAuthStore((s) => s.admin)
  const accessToken = useAuthStore((s) => s.accessToken)
  const apiBaseUrl = useEnvStore((s) => s.apiBaseUrl)

  const { data: server, isFetched } = useQuery({
    queryKey: queryKeys.adminPermissions(admin?._id ?? '', apiBaseUrl),
    queryFn: fetchPermissions,
    enabled: !!accessToken && !!admin,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const permissions = useMemo<PermissionMap>(
    () => ({ ...fallbackPermissionsFor(admin?.role), ...(server?.permissions ?? {}) }),
    [admin?.role, server],
  )

  const level = useCallback((section: AdminSection) => permissions[section] ?? 'none', [permissions])
  const can = useCallback(
    (section: AdminSection, action: AdminAction) => levelAllows(permissions[section] ?? 'none', action),
    [permissions],
  )
  const homePath = useMemo(() => firstAllowedPath(permissions), [permissions])

  const canSetVerificationBadges = server?.verificationBadges ?? can('users', 'approve')

  return {
    permissions,
    level,
    can,
    homePath,
    apiHasPermissions: server != null,
    canSetVerificationBadges,
    isControlAdmin: server?.adminControl === true,
    canSeeSupplyInsights: (server?.supplyInsights ?? server?.adminControl) === true,
    apiHasAdminControl: server?.adminControl != null,
    apiRoles: server?.roles ?? null,
    permissionsLoaded: isFetched,
  }
}

/** Shorthand for a page that only needs `can` for its own section. */
export function useCan(section: AdminSection) {
  const { can } = usePermissions()
  return useCallback((action: AdminAction) => can(section, action), [can, section])
}
